/* GASSISA
=========================================================================================
* Descripción: 
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 01/03/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 03/06/2026 | Lic. José Luis Aragón Cervantes  | Mejora scroll y agrega fecha venta a reporte
* --------------------------------------------------------------------------------------- 
*/

import TextField from "../ui/TextField";
import Autocomplete from "../ui/Autocomplete";
import React, { useState, useMemo, useEffect, forwardRef, Fragment } from "react";
import { Paper, Typography, Box, Stack, Button } from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Componentes de Layout y UI del sistema
import SizeSoftXLayout from "../leyout/SizeSoftXLayout";
import SizeSoftXTableSinPaginado from "../leyout/SizeSoftXTableSinPaginado";
import SizeSoftXLoadingProcess from "../leyout/SizeSoftXLoadingProcess";
import MnuReportesVentasAlmacenActions from "./MnuReportesVentasAlmacenActions";

// Contextos
import { useInventarios } from "../../context/Inventarios/InventariosContext";
import { useVentas } from "../../context/Ventas/VentasContext";
import { useFormTabLock } from "../../hooks/useFormTabLock";

const CustomDateInput = forwardRef(function CustomDateInput(props, ref) {
  const { value, onClick, label } = props;

  return (
    <TextField autoComplete="off"
      label={label}
      size="small"
      value={value || ""}
      onClick={onClick}
      inputRef={ref}
      fullWidth
      InputProps={{
        readOnly: true,
      }}
      sx={{ minWidth: 170 }}
    />
  );
});

const normalizeStartDate = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const normalizeEndDate = (date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

const formatDateOnly = (date) => {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const obtenerFechaVentaReporte = (row) => (
  row?.fechaventa
  || row?.fechaVenta
  || row?.FechaVenta
  || row?.fecharegistro
  || row?.fechaRegistro
  || row?.FechaRegistro
  || row?.dteFechaRegistro
  || ""
);

export default function FrmReporteVentasAlmacen() {
  const { isLocked: isFormLocked } = useFormTabLock('reporte_ventas_almacen', {
    title: 'Reporte Ventas Almacén',
    onLockDetected: (info) => {
      console.warn(`El formulario de ${info?.title || 'Reporte Ventas Almacén'} ya está abierto en otra pestaña`);
    }
  });

  const { listaAlmacenesExhibidoresGeneral, handleObtenerAlmacenesExhibidores } = useInventarios();
  const { statusWaiting, handleObtenerVentasPorPeriodoAlmacen, handleClickRegresarMainDashboard, 
    fechaInicioVentas, setFechaInicioVentas, fechaFinVentas, setFechaFinVentas,almacenSeleccionadoVentas, setAlmacenSeleccionadVentas,filtroArticuloVentas, setFiltroArticuloVentas,
    listaVentasAgrupadas, setListaVentasAgrupadas,
  } = useVentas();

  const [articuloSelecccionado, setArticuloSeleccionado] = useState(null);

  const handleChangeFechaInicio = (date) => {
    if (!date) return;
    const nuevaFecha = normalizeStartDate(date);
    setFechaInicioVentas(nuevaFecha);

    if (nuevaFecha > fechaFinVentas) {
      setFechaFinVentas(normalizeEndDate(nuevaFecha));
    }
  };

  const handleChangeFechaFin = (date) => {
    if (!date) return;
    const nuevaFecha = normalizeEndDate(date);
    setFechaFinVentas(nuevaFecha);

    if (nuevaFecha < fechaInicioVentas) {
      setFechaInicioVentas(normalizeStartDate(nuevaFecha));
    }
  };

  useEffect(() => {
    handleObtenerAlmacenesExhibidores("GENERAL");
  }, []);

  useEffect(() => {
    if (isFormLocked) {
      alert("El formulario de Reporte Ventas Almacén ya está abierto en otra pestaña del navegador.\n\nPor favor, cierre la otra ventana o pestaña para continuar.");
      setTimeout(() => {
        handleClickRegresarMainDashboard();
      }, 900);
    }
  }, [isFormLocked]);

  const handleConsultar = async () => {
    const params = {
      fechaInicio: formatDateOnly(fechaInicioVentas),
      fechaFin: formatDateOnly(fechaFinVentas),
      almacen: almacenSeleccionadoVentas?.almacenexhibidor ?? "",
      filtro: filtroArticuloVentas?.trim() ?? "",
    };


    const data = await handleObtenerVentasPorPeriodoAlmacen(params);
    setListaVentasAgrupadas(data || []);
  };

  const ventasFiltradas = useMemo(() => {
    if (!filtroArticuloVentas?.trim()) return listaVentasAgrupadas;
    
    
    const filtro = filtroArticuloVentas.toLowerCase();

    return listaVentasAgrupadas.filter((item) =>
      (item.descripcion || "").toLowerCase().includes(filtro) ||
      (item.clavearticulo || "").toLowerCase().includes(filtro)
    );
  }, [filtroArticuloVentas, listaVentasAgrupadas]);

  const ventasReporte = useMemo(() => (
    ventasFiltradas.map((item) => ({
      ...item,
      fechaventa: obtenerFechaVentaReporte(item),
    }))
  ), [ventasFiltradas]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        handleConsultar();
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [fechaInicioVentas, fechaFinVentas, almacenSeleccionadoVentas, filtroArticuloVentas]);

  return (
      <SizeSoftXLayout
        titlePage="Reporte por Almacén Ventas ó Reposiciones"
        buttonActions={
          <Fragment>
            <MnuReportesVentasAlmacenActions
              onRegresar={handleClickRegresarMainDashboard}
              almacenSeleccionado={almacenSeleccionadoVentas}
              fechaInicio={fechaInicioVentas}
              fechaFin={fechaFinVentas}
              ventasFiltradas={ventasFiltradas}
            />
          </Fragment>
        }
      >
        <Box sx={{ p: 3 }}>
        <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
            flexWrap="wrap"
          >
            <DatePicker
              selected={fechaInicioVentas}
              onChange={handleChangeFechaInicio}
              dateFormat="dd-MM-yyyy"
              customInput={<CustomDateInput label="Fecha Inicio" />}
            />

            <DatePicker
              selected={fechaFinVentas}
              onChange={handleChangeFechaFin}
              dateFormat="dd-MM-yyyy"
              customInput={<CustomDateInput label="Fecha Fin" />}
            />

            <Autocomplete
              options={listaAlmacenesExhibidoresGeneral || []}
              getOptionLabel={(opt) => opt?.almacenexhibidor || ""}
              value={almacenSeleccionadoVentas}
              onChange={(e, newVal) => setAlmacenSeleccionadVentas(newVal)}
              isOptionEqualToValue={(option, value) =>
                option?.almacenexhibidor === value?.almacenexhibidor
              }
              sx={{ minWidth: 260 }}
              renderInput={(params) => (
                <TextField
                  autoComplete="off"
                  {...params}
                  label="Almacén / Exhibidor"
                  size="small"
                />
              )}
            />

            <TextField autoComplete="off"
              label="Buscar por descripción/clave"
              size="small"
              value={filtroArticuloVentas}
              onChange={(e) => setFiltroArticuloVentas(e.target.value)}
              sx={{ minWidth: 240 }}
            />

            <Button variant="contained" onClick={handleConsultar}>
              Consultar
            </Button>
          </Stack>
        </Paper>

        <Paper elevation={3} sx={{ height: "60vh", width: "100%" }}>
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ padding: '8px 16px', backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                LISTA DE VENTAS FILTRADAS
              </Typography>
            </div>
            <div className="reporte-reposiciones-tabla" style={{ flex: 1 }}>
              <SizeSoftXTableSinPaginado
                columns={[
                  {
                    title: "Fecha venta",
                    field: "fechaventa",
                    width: 150,
                    render: (row) => obtenerFechaVentaReporte(row),
                  },
                  { title: "Almacén", field: "almacenexhibidor", width: 150 },
                  { title: "Vendedor", field: "vendedorpreventa", width: 180 },
                  { title: "Descripción", field: "descripcion", width: 280 },
                  { title: "Medida", field: "medida", width: 100 },
                  { title: "Vendidos", field: "cantidadvendida", width: 120, type: "numeric" },
                  { title: "Factor", field: "factor", width: 100 },
                  { title: "Familia", field: "familia", width: 130 },
                  { title: "Subfamilia", field: "subfamilia", width: 150 },
                  { title: "Presentación", field: "presentacion", width: 130 },
                  { title: "Fabricante", field: "fabricante", width: 150 },
                  { title: "Costo Compra", field: "costocompra", width: 130 },
                  { title: "Costo Venta", field: "costoventa", width: 130 },
                  { title: "Clave", field: "clavearticulo", width: 150 },
                ]}
                id={"id"}
                data={ventasReporte}
                row={articuloSelecccionado}
                setRow={setArticuloSeleccionado}
                fontSize="small"
                EspaciadorColumnas={1}
                maxHeight="calc(60vh - 50px)"
              />
            </div>
          </div>
        </Paper>
      </Box>

      <SizeSoftXLoadingProcess open={statusWaiting} />
    </SizeSoftXLayout>
  );
}
